import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Timer, Play, Pause, RotateCcw, ChevronRight, ChevronLeft, Download } from "lucide-react";

const STEPS = [
  {
    key: "attention",
    letter: "A",
    label: "Attention",
    color: "#4aa0a4",
    blurb: "Notice what you're working on — and what you're assuming about it.",
  },
  {
    key: "witness",
    letter: "W",
    label: "Witness",
    color: "#f4b847",
    blurb: "Step into the world of the person it's really for.",
  },
  {
    key: "align",
    letter: "A",
    label: "Align",
    color: "#4aa0a4",
    blurb: "Choose the most honest framing of the problem.",
  },
  {
    key: "kata",
    letter: "K",
    label: "Kata",
    color: "#e93e36",
    blurb: "Spar with the idea before you build it.",
  },
  {
    key: "evolve",
    letter: "E",
    label: "Evolve",
    color: "#3c8386",
    blurb: "Commit to a 90-day plan you'll actually do.",
  },
];

const DEFAULT_SECONDS = 15 * 60;

function BigTimer() {
  const [seconds, setSeconds] = useState(DEFAULT_SECONDS);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(ref.current);
          setRunning(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const warn = running && seconds <= 60;

  return (
    <div className="weave-big-timer" data-testid="facilitate-big-timer">
      <div className="weave-big-timer-label">
        <Timer size={20} />
        Workshop timer
      </div>
      <div
        className={`weave-big-timer-display ${warn ? "is-warning" : ""}`}
        data-testid="facilitate-big-timer-display"
      >
        {seconds === 0 && !running ? "00:00" : `${mm}:${ss}`}
      </div>
      <div className="weave-big-timer-actions">
        {!running ? (
          <button
            type="button"
            className="weave-btn-primary"
            onClick={() => {
              if (seconds === 0) setSeconds(DEFAULT_SECONDS);
              setRunning(true);
            }}
            data-testid="facilitate-timer-start"
          >
            <Play size={16} /> Start
          </button>
        ) : (
          <button
            type="button"
            className="weave-btn-outline"
            onClick={() => setRunning(false)}
            data-testid="facilitate-timer-pause"
          >
            <Pause size={16} /> Pause
          </button>
        )}
        <button
          type="button"
          className="weave-btn-outline"
          onClick={() => {
            setRunning(false);
            setSeconds(DEFAULT_SECONDS);
          }}
          data-testid="facilitate-timer-reset"
        >
          <RotateCcw size={16} /> Reset
        </button>
      </div>
    </div>
  );
}

export default function Facilitate() {
  const [phase, setPhase] = useState("teach"); // "teach" | "share"
  const url =
    typeof window !== "undefined" ? window.location.origin : "https://stay-awake";
  const displayUrl = url.replace(/^https?:\/\//, "");

  return (
    <div className="weave-facilitate" data-testid="facilitate-page">
      <header className="weave-facilitate-header">
        <a href="/" className="weave-logo-link" aria-label="Back to Stay AWAKE">
          <img src="/the-weave-logo.png" alt="The Weave" className="weave-logo-img" />
        </a>
        <span
          className="text-[11px] uppercase tracking-[0.22em] font-medium"
          style={{ color: "#3c8386" }}
          data-testid="facilitate-mode-badge"
        >
          Facilitator · Projector view
        </span>
      </header>

      {phase === "teach" && (
        <main className="weave-facilitate-main" data-testid="facilitate-teach-view">
          <section className="weave-facilitate-hero">
            <h1 className="weave-facilitate-h1">
              Stay <span className="weave-underline">AWAKE</span>
            </h1>
            <p className="weave-facilitate-sub">
              Five steps. Fifteen minutes. One honest plan.
            </p>
          </section>

          <section
            className="weave-awake-large"
            data-testid="facilitate-framework"
          >
            {STEPS.map((s, i) => (
              <div key={s.key} className="weave-awake-step">
                <span
                  className="weave-awake-letter"
                  style={{ background: s.color }}
                >
                  {s.letter}
                </span>
                <div>
                  <h3>{s.label}</h3>
                  <p>{s.blurb}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <span className="weave-awake-arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </div>
            ))}
          </section>

          <section className="weave-facilitate-controls">
            <BigTimer />
            <div className="weave-facilitate-qr" data-testid="facilitate-qr">
              <div className="weave-facilitate-qr-frame">
                <QRCodeSVG
                  value={url}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#1e293b"
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="weave-facilitate-qr-label">
                Scan with your phone to start your own AWAKE journey
              </p>
              <p className="weave-facilitate-qr-url">{displayUrl}</p>
            </div>
          </section>

          <section className="weave-facilitate-footer">
            <a
              href="/Stay_AWAKE_workshop_slides.pptx"
              download
              className="weave-btn-outline"
              data-testid="download-slides-btn"
            >
              <Download size={16} /> Download workshop slides
            </a>
            <button
              type="button"
              className="weave-btn-primary"
              onClick={() => setPhase("share")}
              data-testid="facilitate-share-toggle"
            >
              Move to sharing
              <ChevronRight size={16} />
            </button>
          </section>
        </main>
      )}

      {phase === "share" && (
        <main
          className="weave-facilitate-main weave-facilitate-share-view"
          data-testid="facilitate-share-view"
        >
          <section className="weave-facilitate-hero">
            <p
              className="text-[11px] uppercase tracking-[0.22em] font-medium"
              style={{ color: "#b8332c" }}
            >
              Now sharing
            </p>
            <h1 className="weave-facilitate-h1">
              Find your <span className="weave-underline">partner</span>.
            </h1>
            <p className="weave-facilitate-sub">
              Take turns reading your spoken contract aloud.
              <br />
              Listen without interrupting. Then swap.
            </p>
          </section>

          <section className="weave-share-prompts">
            <ol>
              <li>
                <span className="weave-share-step">1</span>
                <p>Pair up with the person next to you.</p>
              </li>
              <li>
                <span className="weave-share-step">2</span>
                <p>
                  Decide who reads first. The other listens — really listens.
                </p>
              </li>
              <li>
                <span className="weave-share-step">3</span>
                <p>Read your spoken contract aloud, end to end.</p>
              </li>
              <li>
                <span className="weave-share-step">4</span>
                <p>Swap. Then thank each other.</p>
              </li>
            </ol>
          </section>

          <section className="weave-facilitate-footer">
            <button
              type="button"
              className="weave-btn-outline"
              onClick={() => setPhase("teach")}
              data-testid="facilitate-back-to-teach"
            >
              <ChevronLeft size={16} /> Back to framework
            </button>
          </section>
        </main>
      )}
    </div>
  );
}
